import type { ApiResponse } from 'src/api/services/api.types';
import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';
import type { UpdatePolicyUnitStatReportRequest } from 'src/api/services/policy.service';
import type { UnitStatReportSummaryViewModel } from 'src/api/types/UnitStatReportSummaryViewModel';

import { useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'react-router-dom';
import { memo, useRef, useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { Box, Tab, Card, Typography, CardContent } from '@mui/material';

import { useClassCodeLookups } from 'src/api/hooks/usePolicyQueries';
import {
  useStateLookups,
  useUnitStatUsrLookups,
  useUnitStatReportVersionsByPolicyId,
  useUnitStatReportByPolicyIdAndReportNumber,
} from 'src/api/hooks/usePolicyDetailsQueries';

import { EmptyContent } from 'src/components/empty-content';

import UnitStatHeader from './tabs/header/unit-stat-header';
import UnitStatLosses from './tabs/losses/unit-stat-losses';
import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import UnitStatHeaderEdit from './tabs/header/unit-stat-header-edit';
import UnitStatExposures from './tabs/exposures/unit-stat-exposures';
import {
  createUnitStatExposureDraft,
  getUnitStatExposureDraftDelta,
  validateUnitStatExposureDraft,
  createUnitStatExposureRequests,
  applyUnitStatExposureDraftDelta,
  type UnitStatExposureDraftDelta,
  getUnitStatExposureChangedFieldSummaries,
} from './tabs/exposures/unit-stat-exposures-edit.helpers';
import {
  type UnitStatHeaderDraft,
  createUnitStatHeaderDraft,
  type UnitStatHeaderLookups,
  getUnitStatHeaderDraftDelta,
  validateUnitStatHeaderDraft,
  applyUnitStatHeaderDraftDelta,
  type UnitStatReportsDraftDelta,
  type UnitStatHeaderFieldErrors,
  createUnitStatHeaderUpdateRequest,
  getUnitStatHeaderChangedFieldSummaries,
} from './tabs/header/unit-stat-header-edit.helpers';

// The errored row's exact position is unreliable (inner-tab switch + a split's own collapse-reopen
// effect both need to land first), so just scroll to the bottom of the page instead of the row itself.
const scrollToPageBottom = () => {
  window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
};

export type UnitStatReportsHandle = {
  validateForSave: () => boolean; 
  hasPendingChanges: () => boolean;
  getChangedFieldSummaries: () => string[];
  getUnitStatReportsPayload: () => UpdatePolicyUnitStatReportRequest[];
  commitEdit: () => void;
  cancelEdit: () => void;
};

type UnitStatReportsProps = {
  isEditMode?: boolean;
  initialDraftDelta?: UnitStatReportsDraftDelta | null;
  onDraftDeltaChange?: (delta: UnitStatReportsDraftDelta) => void;
};

const UnitStatReports = forwardRef<UnitStatReportsHandle, UnitStatReportsProps>(
  function UnitStatReportsRenderer(
    { isEditMode = false, initialDraftDelta = null, onDraftDeltaChange },
    ref
  ) {
    const location = useLocation();
    const { comboId, coverageId, policyId } = useParams<{
      comboId: string;
      coverageId: string;
      policyId: string;
    }>();
    const { data: reportVersions } = useUnitStatReportVersionsByPolicyId(
      Number(comboId),
      Number(coverageId),
      Number(policyId)
    );
    const queryClient = useQueryClient();
    const headerWidth = useHeaderWidth();
    const [outerTab, setOuterTab] = useState('1.0');
    const [innerTab, setInnerTab] = useState((location.state as any)?.innerTab || 'Header');
    const [headerDraft, setHeaderDraft] = useState<UnitStatHeaderDraft>(() =>
      createUnitStatHeaderDraft(undefined)
    );
    const [headerSnapshot, setHeaderSnapshot] = useState<UnitStatHeaderDraft | null>(null);
    const [headerErrors, setHeaderErrors] = useState<UnitStatHeaderFieldErrors>({});
    const [exposureDraft, setExposureDraft] = useState<UnitStatExposureDraftDelta>({ rows: [], deletedExposureIds: [] });
    const [exposureSnapshot, setExposureSnapshot] = useState<UnitStatExposureDraftDelta | null>(null);
    const [exposureErrors, setExposureErrors] = useState<Record<string, string>>({});
    const { data: usrLookups } = useUnitStatUsrLookups();
    const { data: stateLookups } = useStateLookups();
    const { data: classCodeLookups } = useClassCodeLookups();
    // Per-correction (outerTab) delta cache, so switching between correction tabs doesn't lose
    // in-progress edits the way a single shared delta blob would.
    const draftDeltasByCorrectionRef = useRef<UnitStatReportsDraftDelta>({
      ...(initialDraftDelta ?? {}),
    });
    // Tracks the last report reference actually processed, so a mount-effect re-invocation with
    // unchanged data (e.g. Strict Mode's double-invoke) is a no-op instead of re-resetting the draft.
    const lastProcessedReportRef = useRef<unknown>(undefined);

    useEffect(() => {
      if ((location.state as any)?.innerTab) setInnerTab((location.state as any).innerTab);
    }, [location.state]);

    useEffect(() => {
      if (reportVersions?.data?.length) {
        const stateReportNumber = location.state?.reportNumber;
        const stateCorrSeq = location.state?.correctionSequenceNumber;
        const first = reportVersions.data[0];
        setOuterTab(
          stateReportNumber !== undefined && stateCorrSeq !== undefined
            ? stateReportNumber + '.' + stateCorrSeq
            : first.reportNumber + '.' + first.correctionSequenceNumber
        );
      }
    }, [reportVersions, location.state]);

    const selectedVersion: UnitStatReportSummaryViewModel | undefined = reportVersions?.data?.find(
      (version) => outerTab === version.reportNumber + '.' + version.correctionSequenceNumber
    );
    // Only Report 1.0 (the original, uncorrected report) can be edited; every other correction
    // version is read-only, including its exposures.
    const isEditableReport =
      selectedVersion?.reportNumber === 1 && selectedVersion.correctionSequenceNumber === 0;
    const { data: unitStatReport, isFetching: isReportFetching } =
      useUnitStatReportByPolicyIdAndReportNumber(
        Number(comboId),
        Number(coverageId),
        Number(policyId),
        selectedVersion?.reportNumber,
        selectedVersion?.correctionSequenceNumber
      );
    const report = unitStatReport?.data;

    const getPendingCorrectionKeys = () => Object.keys(draftDeltasByCorrectionRef.current);
    const getEditablePendingCorrectionKeys = () =>
      getPendingCorrectionKeys().filter((key) => key === '1.0');
    const lookups = useMemo<UnitStatHeaderLookups>(
      () => ({
        states: stateLookups?.data ?? [],
        correctionTypes: usrLookups?.data?.correctionTypes ?? [],
        coverageTypes: usrLookups?.data?.coverageTypes ?? [],
        marketTypes: usrLookups?.data?.marketTypes ?? [],
        estimatedAuditTypes: usrLookups?.data?.estimatedAuditTypes ?? [],
        lossesSubjectDeductibleTypes: usrLookups?.data?.lossesSubjectDeductibleTypes ?? [],
        basisDeductibleCalculationTypes: usrLookups?.data?.basisDeductibleCalculationTypes ?? [],
        lossTypes: usrLookups?.data?.lossTypes ?? [],
        injuryTypes: usrLookups?.data?.injuryTypes ?? [],
        claimStatusTypes: usrLookups?.data?.claimStatusTypes ?? [],
        unitStatusTypes: usrLookups?.data?.unitStatusTypes ?? [],
      }),
      [stateLookups?.data, usrLookups?.data]
    );

    // Combined into one effect (rather than a separate "reset" + "report delta" effect) so the
    // delta computation never runs against a headerDraft that hasn't been reconciled with a just-
    // changed report yet - that cross-effect staleness was the root cause of edits going missing
    // when switching correction tabs (a "new report" pass here always returns before comparing).
    useEffect(() => {
      const correctionKey = outerTab;
      if (lastProcessedReportRef.current !== report) {
        lastProcessedReportRef.current = report;
        const freshSnapshot = createUnitStatHeaderDraft(report);
        const cachedDelta = draftDeltasByCorrectionRef.current[correctionKey] ?? null;
        const { exposures: cachedExposures, ...cachedHeaderDelta } = cachedDelta ?? {};
        const freshExposureSnapshot = createUnitStatExposureDraft(report?.unitStatReportExposures ?? []);
        setHeaderDraft(
          report ? applyUnitStatHeaderDraftDelta(freshSnapshot, cachedHeaderDelta) : freshSnapshot
        );
        setExposureDraft(
          report ? applyUnitStatExposureDraftDelta(freshExposureSnapshot, cachedExposures) : freshExposureSnapshot
        );
        setHeaderSnapshot(null);
        setExposureSnapshot(null);
        setHeaderErrors({});
        setExposureErrors({});
        return;
      }

      if (!report) {
        return;
      }

      const delta = getUnitStatHeaderDraftDelta(createUnitStatHeaderDraft(report), headerDraft);
      const exposureDelta = getUnitStatExposureDraftDelta(
        createUnitStatExposureDraft(report.unitStatReportExposures ?? []),
        exposureDraft
      );
      if (Object.keys(delta).length > 0 || exposureDelta) {
        draftDeltasByCorrectionRef.current[correctionKey] = {
          ...delta,
          ...(exposureDelta ? { exposures: exposureDelta } : {}),
        };
      } else {
        delete draftDeltasByCorrectionRef.current[correctionKey];
      }
      onDraftDeltaChange?.({ ...draftDeltasByCorrectionRef.current });
    }, [report, outerTab, headerDraft, exposureDraft, onDraftDeltaChange]);

    useEffect(() => {
      setHeaderSnapshot(isEditMode ? (current) => current ?? headerDraft : null);
      setExposureSnapshot(isEditMode ? (current) => current ?? exposureDraft : null);
      if (!isEditMode) setHeaderErrors({});
      if (!isEditMode) setExposureErrors({});
    }, [exposureDraft, headerDraft, isEditMode]);

    useImperativeHandle(ref, () => {
      const getExposurePendingChanges = () => {
        if (!isEditableReport || !report) {
          return false;
        }

        const exposureBaseline = exposureSnapshot ?? createUnitStatExposureDraft(report.unitStatReportExposures ?? []);
        return getUnitStatExposureDraftDelta(exposureBaseline, exposureDraft) !== null;
      };

      const hasPendingChanges = () =>
        (isEditableReport &&
          ((headerSnapshot !== null &&
            JSON.stringify(headerSnapshot) !== JSON.stringify(headerDraft)) ||
            getExposurePendingChanges())) ||
        getEditablePendingCorrectionKeys().some((key) => key !== outerTab);

      // Each correction tab fetches and caches its own report data when visited, so any correction
      // with a pending edit already has its report available here.
      const getReportForCorrection = (
        correctionKey: string
      ): UnitStatReportViewModel | undefined => {
        if (correctionKey === outerTab) {
          return report;
        }

        const [reportNumberPart, correctionSequenceNumberPart] = correctionKey.split('.');
        return queryClient.getQueryData<ApiResponse<UnitStatReportViewModel>>([
          'policyUnitStatReport',
          Number(comboId),
          Number(coverageId),
          Number(policyId),
          Number(reportNumberPart),
          Number(correctionSequenceNumberPart),
        ])?.data;
      };

      const getDraftForCorrection = (
        correctionKey: string
      ): { report: UnitStatReportViewModel; draft: UnitStatHeaderDraft } | undefined => {
        const correctionReport = getReportForCorrection(correctionKey);
        if (!correctionReport) {
          return undefined;
        }

        if (correctionKey === outerTab) {
          return { report: correctionReport, draft: headerDraft };
        }

        const snapshot = createUnitStatHeaderDraft(correctionReport);
        const delta = draftDeltasByCorrectionRef.current[correctionKey] ?? null;
        return { report: correctionReport, draft: applyUnitStatHeaderDraftDelta(snapshot, delta) };
      };

      const getExposureDraftForCorrection = (correctionKey: string) => {
        const correctionReport = getReportForCorrection(correctionKey);
        if (!correctionReport) return undefined;
        const snapshot = createUnitStatExposureDraft(correctionReport.unitStatReportExposures ?? []);
        const delta = draftDeltasByCorrectionRef.current[correctionKey]?.exposures;
        return {
          report: correctionReport,
          draft: correctionKey === outerTab ? exposureDraft : applyUnitStatExposureDraftDelta(snapshot, delta),
          snapshot,
        };
      };

      return {
        validateForSave: () => {
          const errors = validateUnitStatHeaderDraft(
            headerDraft,
            report?.correctionSequenceNumber,
            headerSnapshot ?? undefined
          );
          setHeaderErrors(errors);
          const exposureValidationErrors = validateUnitStatExposureDraft(
            exposureDraft,
            classCodeLookups?.data ?? []
          );
          setExposureErrors(exposureValidationErrors);
          const firstExposureErrorKey = Object.keys(exposureValidationErrors)[0];
          if (firstExposureErrorKey) {
            // The Exposures inner tab panel isn't mounted unless it's selected, so switch to it first.
            setInnerTab('Exposures');
            requestAnimationFrame(() => requestAnimationFrame(scrollToPageBottom));
          }
          if (Object.keys(errors).length > 0 || Object.keys(exposureValidationErrors).length > 0) {
            return false;
          }

          // Other correction tabs with cached pending edits are validated here too (against their own
          // snapshot), since the save API now accepts every edited correction in one request.
          return getEditablePendingCorrectionKeys()
            .filter((key) => key !== outerTab)
            .every((key) => {
              const entry = getDraftForCorrection(key);
              if (!entry) {
                return true;
              }

              const snapshot = createUnitStatHeaderDraft(entry.report);
              const exposureEntry = getExposureDraftForCorrection(key);
              return (
                Object.keys(
                  validateUnitStatHeaderDraft(
                    entry.draft,
                    entry.report.correctionSequenceNumber,
                    snapshot
                  )
                ).length === 0 &&
                (!exposureEntry || Object.keys(validateUnitStatExposureDraft(exposureEntry.draft, classCodeLookups?.data ?? [])).length === 0)
              );
            });
        },
        hasPendingChanges,
        getChangedFieldSummaries: () => {
          if (!hasPendingChanges()) {
            return [];
          }

          const currentHasChanges =
            report !== undefined &&
            headerSnapshot !== null &&
            JSON.stringify(headerSnapshot) !== JSON.stringify(headerDraft);
          const summaries = currentHasChanges
            ? getUnitStatHeaderChangedFieldSummaries(report!, headerDraft, lookups).map(
                (line) => `Correction ${outerTab} ${line}`
              )
            : [];
          if (getExposurePendingChanges()) {
            const exposureBaseline = exposureSnapshot ?? createUnitStatExposureDraft(report?.unitStatReportExposures ?? []);
            getUnitStatExposureChangedFieldSummaries(
              exposureBaseline,
              exposureDraft,
              usrLookups?.data?.splitPeriodTypes ?? []
            ).forEach((line) => summaries.push(`Correction ${outerTab} ${line}`));
          }

          // Other correction tabs with cached, pending edits are now included in the save request too,
          // so their changed fields are summarized here alongside the active tab's.
          getEditablePendingCorrectionKeys()
            .filter((key) => key !== outerTab)
            .forEach((key) => {
              const entry = getDraftForCorrection(key);
              if (!entry) {
                return;
              }

              getUnitStatHeaderChangedFieldSummaries(entry.report, entry.draft, lookups).forEach(
                (line) => summaries.push(`Correction ${key} ${line}`)
              );
              if (draftDeltasByCorrectionRef.current[key]?.exposures) {
                const exposureEntry = getExposureDraftForCorrection(key);
                if (exposureEntry) {
                  getUnitStatExposureChangedFieldSummaries(
                    exposureEntry.snapshot,
                    exposureEntry.draft,
                    usrLookups?.data?.splitPeriodTypes ?? []
                  ).forEach((line) => summaries.push(`Correction ${key} ${line}`));
                }
              }
            });

          return summaries;
        },
        getUnitStatReportsPayload: () => {
          const payloads: UpdatePolicyUnitStatReportRequest[] = [];
          const currentHeaderChanged =
            report &&
            Object.keys(getUnitStatHeaderDraftDelta(createUnitStatHeaderDraft(report), headerDraft))
              .length > 0;
          const currentExposureChanged =
            report &&
            (exposureSnapshot === null || JSON.stringify(exposureSnapshot) !== JSON.stringify(exposureDraft));
          if (report && (currentHeaderChanged || currentExposureChanged)) {
            const request = createUnitStatHeaderUpdateRequest(report, headerDraft);
            if (currentExposureChanged) {
              request.exposures = createUnitStatExposureRequests(exposureDraft);
              request.deletedExposureIds = exposureDraft.deletedExposureIds;
            }
            payloads.push(request);
          }

          getEditablePendingCorrectionKeys()
            .filter((key) => key !== outerTab)
            .forEach((key) => {
              const entry = getDraftForCorrection(key);
              if (entry) {
                const request = createUnitStatHeaderUpdateRequest(entry.report, entry.draft);
                const exposureEntry = getExposureDraftForCorrection(key);
                if (exposureEntry) {
                  request.exposures = createUnitStatExposureRequests(exposureEntry.draft);
                  request.deletedExposureIds = exposureEntry.draft.deletedExposureIds;
                }
                payloads.push(request);
              }
            });

          return payloads;
        },
        commitEdit: () => {
          setHeaderSnapshot(headerDraft);
          const committedExposureDraft = { ...exposureDraft, deletedExposureIds: [] };
          setExposureDraft(committedExposureDraft);
          setExposureSnapshot(committedExposureDraft);
          delete draftDeltasByCorrectionRef.current[outerTab];
          onDraftDeltaChange?.({ ...draftDeltasByCorrectionRef.current });
        },
        cancelEdit: () => {
          setHeaderDraft(createUnitStatHeaderDraft(report));
          setExposureDraft(createUnitStatExposureDraft(report?.unitStatReportExposures ?? []));
          setHeaderSnapshot(null);
          setExposureSnapshot(null);
          setHeaderErrors({});
          setExposureErrors({});
          draftDeltasByCorrectionRef.current = {};
          onDraftDeltaChange?.({});
        },
      };
    }, [
      comboId,
      coverageId,
      headerDraft,
      headerSnapshot,
      lookups,
      onDraftDeltaChange,
      outerTab,
      policyId,
      queryClient,
      report,
      isEditableReport,
      classCodeLookups,
      exposureDraft,
      exposureSnapshot,
      exposureErrors,
      usrLookups?.data?.splitPeriodTypes,
    ]);

    return (
      <Card
        sx={{
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: 5,
          borderRadius: 2,
          boxShadow: 'var(--customShadows-card)',
          width: headerWidth,
        }}
      >
        <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="policy-name-addresses-card">
          <Box sx={{ padding: '1.5rem' }}>
            <Typography
              variant="h5"
              sx={{ color: 'text.primary', mb: 2.5 }}
              data-testid="primary-names-addresses-title"
            >
              Unit statistical reports
            </Typography>
            {reportVersions?.data?.length ? (
              <TabContext value={outerTab}>
                <TabList
                  onChange={(_event, value) => setOuterTab(value)}
                  sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
                  aria-label="report version tabs"
                >
                  {reportVersions.data.map((version) => {
                    const value = version.reportNumber + '.' + version.correctionSequenceNumber;
                    return (
                      <Tab
                        key={value}
                        label={value}
                        value={value}
                        data-testid={`unit-stat-report-tab-${value}`}
                      />
                    );
                  })}
                </TabList>
                {reportVersions.data.map((version) => {
                  const value = version.reportNumber + '.' + version.correctionSequenceNumber;
                  return (
                    <TabPanel key={value} value={value} sx={{ p: 0 }}>
                      <TabContext value={innerTab}>
                        <TabList
                          onChange={(_event, tab) => setInnerTab(tab)}
                          aria-label="unit stat inner tabs"
                          sx={{
                            '& .MuiTabs-indicator': { height: '2.5px' },
                            mb: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Tab label="Header" value="Header" data-testid="Header Tab" />
                          <Tab label="Exposures" value="Exposures" data-testid="Exposures Tab" />
                          <Tab label="Losses" value="Losses" data-testid="Losses Tab" />
                        </TabList>
                        <TabPanel value="Header" sx={{ p: 0 }}>
                          {isEditMode && isEditableReport ? (
                            <UnitStatHeaderEdit
                              data={report}
                              draft={headerDraft}
                              setDraft={setHeaderDraft}
                              lookups={lookups}
                              errors={headerErrors}
                              loading={isReportFetching || !report}
                            />
                          ) : (
                            <UnitStatHeader data={report} />
                          )}
                        </TabPanel>
                        <TabPanel value="Exposures" sx={{ p: 0 }}>
                          <UnitStatExposures
                            data={report}
                            isEditMode={isEditMode && isEditableReport}
                            draft={exposureDraft}
                            setDraft={setExposureDraft}
                            errors={exposureErrors}
                            updateTypes={usrLookups?.data?.updateTypes ?? []}
                            classCodes={classCodeLookups?.data ?? []}
                            splitPeriodTypes={usrLookups?.data?.splitPeriodTypes ?? []}
                          />
                        </TabPanel>
                        <TabPanel value="Losses" sx={{ p: 0 }}>
                          <UnitStatLosses data={report} />
                        </TabPanel>
                      </TabContext>
                    </TabPanel>
                  );
                })}
              </TabContext>
            ) : (
              <EmptyContent
                sx={{ mt: 2 }}
                description="Unit statistical reports have not yet been received for this policy."
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }
);

export default memo(UnitStatReports);
